import { TableBody, TableHead, TableRow } from '@mui/material';
import dayjs from 'dayjs';
import DOMPurify from 'dompurify';
import { CSSProperties, forwardRef, useEffect } from 'react';
import { Base64Image } from 'src/components';
import type {
  PricingExportColumn,
  PricingExportRow,
} from 'src/pages/Settings/memberships/utils/build-pricing-export-grid';
import {
  ExportDate,
  OneVisionLogo,
  PartnerLogo,
  PricingBodyCell,
  PricingExportBody,
  PricingExportHeader,
  PricingExportRoot,
  PricingFooter,
  PricingFootnote,
  PricingHeadCell,
  PricingHeader,
  PricingSubheader,
  PricingTable,
} from './styled';

export type PricingExportTemplateData = {
  clientSegment: string;
  columns: PricingExportColumn[];
  rows: PricingExportRow[];
  logoUrl?: string | null;
};

export type PricingExportTemplateProps = {
  data: PricingExportTemplateData;
  onTemplateReady?: () => void;
  templateStyle?: CSSProperties;
};

const TEMPLATE_ID = 'membership-pricing-template';
const sanitizeHtml = (value: string) =>
  DOMPurify.sanitize(value, { USE_PROFILES: { html: true } });

export const PricingExportTemplate = forwardRef<HTMLDivElement, PricingExportTemplateProps>(
  ({ data, onTemplateReady, templateStyle }, ref) => {
    const { clientSegment, columns, rows, logoUrl } = data;

    useEffect(() => {
      if (!logoUrl) {
        onTemplateReady?.();
      }
    }, [logoUrl, onTemplateReady]);

    return (
      <PricingExportRoot ref={ref} id={TEMPLATE_ID} style={templateStyle}>
        <PricingExportHeader>
          <OneVisionLogo>
            <img src="/portal-logo.svg" alt="OneVision logo" />
          </OneVisionLogo>
          {logoUrl ? (
            <PartnerLogo>
              <Base64Image
                src={logoUrl}
                onImageLoad={onTemplateReady}
                sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            </PartnerLogo>
          ) : null}
          <ExportDate variant="h2">{dayjs().format('MMMM YYYY')}</ExportDate>
        </PricingExportHeader>

        <PricingExportBody>
          <PricingHeader variant="h1">Support Membership Pricing*</PricingHeader>
          <PricingSubheader variant="h2">{clientSegment}</PricingSubheader>

          <PricingTable>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <PricingHeadCell key={column.field} align={column.align ?? 'left'}>
                    {column.headerName}
                  </PricingHeadCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const rowKey = columns
                  .map((column) => `${column.field}:${row[column.field] ?? ''}`)
                  .join('|');

                return (
                  <TableRow key={rowKey}>
                    {columns.map((column) => (
                      <PricingBodyCell key={column.field} align={column.align ?? 'left'}>
                        {/* Feature content and pricing labels can include HTML from saved config. */}
                        <span
                          dangerouslySetInnerHTML={{
                            __html: sanitizeHtml(row[column.field] ?? ''),
                          }}
                        />
                      </PricingBodyCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </PricingTable>

          <PricingFooter>
            <PricingFootnote variant="h2">*Internal Document</PricingFootnote>
          </PricingFooter>
        </PricingExportBody>
      </PricingExportRoot>
    );
  },
);

PricingExportTemplate.displayName = 'PricingExportTemplate';
